"use client";

import * as React from "react";
import { LoaderCircle, Check, Mail } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const interestOptions = ["Copywriting Tips", "Content Strategy", "Digital Marketing", "Industry Insights"];
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center space-y-6 py-12 animate-in fade-in duration-500">
        <div className="rounded-full bg-[hsl(220_25%_12%)]/10 p-4">
          <Check className="size-8 text-[hsl(220_25%_12%)]" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif italic text-[hsl(220_25%_12%)]">
            You're on the list!
          </h3>
          <p className="text-[hsl(220_25%_12%)]/70 max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="border-[hsl(220_25%_12%)]/20 text-[hsl(220_25%_12%)] hover:bg-[hsl(220_25%_12%)]/5">
          Subscribe another email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(220_25%_12%)]">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" {...field} className="bg-white/50 border-[hsl(220_25%_12%)]/20 focus-visible:ring-[hsl(220_25%_12%)] text-[hsl(220_25%_12%)] placeholder:text-[hsl(220_25%_12%)]/40" />
              </FormControl>
              <FormDescription className="text-[hsl(220_25%_12%)]/50 text-xs">
                Optional – helps us personalize your emails.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" rules={{
        required: false
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(220_25%_12%)]">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} className="bg-white/50 border-[hsl(220_25%_12%)]/20 focus-visible:ring-[hsl(220_25%_12%)] text-[hsl(220_25%_12%)] placeholder:text-[hsl(220_25%_12%)]/40" />
              </FormControl>
              <FormDescription className="text-[hsl(220_25%_12%)]/50 text-xs">
                Optional – adds a personal touch.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[hsl(220_25%_12%)]">Email Address</FormLabel>
            <FormControl>
              <div className="relative">
                <Input type="email" placeholder="jane.doe@example.com" {...field} className="bg-white/50 border-[hsl(220_25%_12%)]/20 focus-visible:ring-[hsl(220_25%_12%)] text-[hsl(220_25%_12%)] placeholder:text-[hsl(220_25%_12%)]/40 pl-10" />
                <Mail className="absolute left-3 top-2.5 size-4 text-[hsl(220_25%_12%)]/40" />
              </div>
            </FormControl>
            <FormDescription className="text-[hsl(220_25%_12%)]/50 text-xs">
              We'll never share your email with third parties.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="interest_topics" rules={{
      required: false
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[hsl(220_25%_12%)] mb-3 block">
              Topics of Interest
            </FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {interestOptions.map((option, i) => <FormItem key={option} data-index={i} className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-[hsl(220_25%_12%)]/10 p-3 bg-white/30 hover:bg-white/60 transition-colors">
                  <FormControl>
                    <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-sm border border-[hsl(220_25%_12%)]/30 text-[hsl(220_25%_12%)] focus:ring-[hsl(220_25%_12%)]/30 focus:ring-offset-0 bg-white cursor-pointer accent-[hsl(220_25%_12%)]" checked={field.value?.includes(option)} onChange={e => {
              const checked = e.target.checked;
              const current = field.value || [];
              if (checked) {
                field.onChange([...current, option]);
              } else {
                field.onChange(current.filter((value: string) => value !== option));
              }
            }} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal text-[hsl(220_25%_12%)] cursor-pointer">
                      {option}
                    </FormLabel>
                  </div>
                </FormItem>)}
            </div>
            <FormDescription className="text-[hsl(220_25%_12%)]/50 text-xs mt-2">
              Select any topics you'd like to hear about.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base bg-[hsl(220_25%_12%)] text-[hsl(40_20%_98%)] hover:bg-[hsl(220_25%_12%)]/90 shadow-md transition-all duration-300">
        {isSubmitting ? <>
            <LoaderCircle className="mr-2 size-4 animate-spin" />
            Subscribing...
          </> : "Subscribe to The Sunday Scribe"}
      </Button>
    </Form>;
}
export default function NewsletterSignup() {
  return <section id="newsletter-signup" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--primary)_0%,_transparent_30%)] opacity-10 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--accent)_0%,_transparent_30%)] opacity-10 pointer-events-none" />

      <div className="max-w-3xl mx-auto relative z-10">
        {}
        <div className="bg-[hsl(40_20%_98%)] text-[hsl(220_25%_12%)] p-8 md:p-16 lg:p-20 shadow-2xl border-4 border-double border-[hsl(220_25%_12%)]/10 mx-auto">
          <div className="text-center mb-12 space-y-4">
            <span className="uppercase tracking-widest text-xs font-semibold text-[hsl(220_25%_12%)]/60">
              Weekly Insights
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif italic text-[hsl(220_25%_12%)] leading-tight">
              The Sunday Scribe
            </h2>
            <p className="text-[hsl(220_25%_12%)]/70 max-w-lg mx-auto text-lg font-light">
              Join our community of entrepreneurs and creators. Get actionable
              copywriting tips and content strategies delivered to your inbox.
            </p>
          </div>

          <FormProvider formId="newsletter_signup_the_content_archive" sectionName="newsletter-signup" defaultValues={{
          first_name: "",
          last_name: "",
          email: "",
          interest_topics: []
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          }
        }} submitText="Subscribe" successMessage="Thank you for subscribing to The Sunday Scribe! Please check your inbox to confirm your subscription.">
            <NewsletterForm />
          </FormProvider>

          <div className="mt-8 pt-8 border-t border-[hsl(220_25%_12%)]/10 text-center">
            <p className="text-xs text-[hsl(220_25%_12%)]/40">
              By subscribing, you agree to our Privacy Policy. You can unsubscribe
              at any time.
            </p>
          </div>
        </div>
      </div>
    </section>;
}